<template>
  <div v-if="show" class="modal-overlay" @click.self="handleClose">
    <div class="modal-content">
      <div class="modal-header">
        <h2>Sign In</h2>
        <button @click="handleClose" class="close-button" aria-label="Close">×</button>
      </div>

      <div class="modal-body">
        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div v-if="loading" class="loading-state">
          <p>Signing in...</p>
        </div>

        <div v-else class="login-options">
          <form @submit.prevent="handleEmailLogin" class="email-form">
            <div class="form-group">
              <label for="email">Email</label>
              <input
                type="email"
                id="email"
                v-model="email"
                required
                placeholder="your.email@example.com"
                class="form-input"
                autocomplete="email"
              />
            </div>

            <div class="form-group">
              <label for="password">Password</label>
              <input
                type="password"
                id="password"
                v-model="password"
                required
                placeholder="Enter your password"
                class="form-input"
                autocomplete="current-password"
              />
            </div>

            <div class="form-actions">
              <button type="submit" class="btn btn-primary" :disabled="loading">
                Sign In with Email
              </button>
            </div>
          </form>

          <div class="forgot-password-link">
            <router-link to="/forgot-password" @click="handleClose">
              Forgot your password?
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loginWithEmail } from '../../services/authService.js'

export default {
  name: 'LoginModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      email: '',
      password: '',
      loading: false,
      error: null
    }
  },
  methods: {
    async handleEmailLogin() {
      this.loading = true
      this.error = null

      try {
        const result = await loginWithEmail(this.email, this.password)
        this.$emit('login-success', result.user)
        this.handleClose()
      } catch (error) {
        this.error = 'Failed to sign in. Please check your email and password.'
        console.error('Email login error:', error)
      } finally {
        this.loading = false
      }
    },
    handleClose() {
      this.email = ''
      this.password = ''
      this.error = null
      this.$emit('close')
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
        this.email = ''
        this.password = ''
        this.error = null
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn var(--transition-fast) ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: var(--color-surface-light);
  border-radius: 12px;
  max-width: 450px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  animation: slideUp var(--transition-normal) ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 2px solid var(--color-border-light);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text-primary);
}

.close-button {
  background: none;
  border: none;
  font-size: 2rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all var(--transition-normal);
}

.close-button:hover {
  background-color: var(--color-surface-dark);
  color: var(--color-text-primary);
}

.close-button:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 2px;
}

.modal-body {
  padding: 30px;
}

.error-message {
  background-color: var(--color-error-light);
  color: var(--color-error-dark);
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid var(--color-error-main);
  font-size: 0.9rem;
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text-secondary);
}

.login-options {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.email-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 1rem;
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border-light);
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  transition: border-color var(--transition-normal);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-main);
}

.form-input:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 2px;
}

.form-actions {
  padding-top: 4px;
}

.btn {
  width: 100%;
  padding: 12px 30px;
  font-size: 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all var(--transition-normal);
}

.btn-primary {
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

.btn-primary:disabled {
  background-color: var(--color-border-main);
  cursor: not-allowed;
}

.btn-primary:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 2px;
}

.forgot-password-link {
  text-align: center;
  margin-top: 10px;
}

.forgot-password-link a {
  color: var(--color-primary-main);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color var(--transition-normal);
}

.forgot-password-link a:hover {
  color: var(--color-primary-dark);
  text-decoration: underline;
}

.demo-note {
  text-align: center;
  color: var(--color-text-light);
  margin-top: 15px;
  font-size: 0.85rem;
}

@media (max-width: 768px) {
  .modal-content {
    max-width: 100%;
  }

  .btn {
    width: 100%;
  }
}
</style>

